<template>
  <div
    v-show="shouldDisplay"
    id="product-case-device"
    :class="[
      'add-to-cart__casedevice__device',
      {
        'pretty-select': !listingSelector && !newDeviceSelectorLayout,
      },
    ]"
  >
    <div v-if="listingSelector && selectedDevice" class="device-selector__hint">
      <span> {{ $t('product_form.sizes') }}: </span>
      <b>{{ selectedDevice.name }}</b>
    </div>
    <ul v-if="listingSelector" class="device-selector__list">
      <li
        v-for="device in orderedOptions"
        :id="inputId"
        :key="device.id"
        :data-slug="device.slug"
        :data-position="device.position"
        :class="[
          'device-selector__item',
          {
            'device-selector__item--active': inputValue === device.id,
            'device-selector__item--without-stock': deviceWithoutStock(device),
          },
        ]"
        @click="
          deviceWithoutStock(device) ? () => ({}) : (inputValue = device.id)
        "
      >
        {{ device.name }}
      </li>
    </ul>
    <new-device-selector-layout
      v-else-if="newDeviceSelectorLayout"
      :brands="brands"
      :product-stock="productStock"
      :case-device-id="caseDeviceId"
      :selected-device="selectedDevice"
      :options="orderedOptions"
      @updateDevice="updateDevice"
      @resetSelectedDevice="inputValue = null"
    />
    <select
      v-else
      :id="inputId"
      v-model="inputValue"
      :name="inputName"
      :class="['pretty-select__select', { required, error: hasErrors }]"
    >
      <option value="">{{ $t('product_form.select_device') }}...</option>
      <option
        v-for="device in orderedOptions"
        :key="device.id"
        :data-slug="device.slug"
        :data-position="device.position"
        :value="device.id"
        :disabled="deviceWithoutStock(device)"
      >
        {{ device.name }}
      </option>
    </select>
    <div v-if="hasErrors" class="form__field-error">
      {{ errors.join(' ') }}
    </div>
  </div>
</template>

<script>
import NewDeviceSelectorLayout from '~/components/product/form/device-selector/new-device-selector-layout/NewDeviceSelectorLayout'
import customInput from '~/mixins/custom-input'
import BaseSelect from '~/components/product/form/BaseSelect'
import sortBy from '~/commun/utils/sortBy'

export default {
  name: 'DeviceSelector',
  components: {
    NewDeviceSelectorLayout,
  },
  extends: BaseSelect,
  mixins: [customInput],
  props: {
    product: {
      type: Object,
      default: null,
    },
    productStock: {
      type: Object,
      default: null,
    },
    caseDeviceId: {
      type: Number,
      default: null,
    },
    newDeviceSelectorLayout: {
      type: Boolean,
      default: false,
    },
    brands: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    selectedDevice() {
      return this.options.find((opt) => opt.id === this.inputValue)
    },
    listingSelector() {
      return this.options.every(
        (opt) =>
          opt.name.endsWith('ml') ||
          opt.name.endsWith('oz') ||
          opt.name.endsWith('"')
      )
    },
    shouldDisplay() {
      return this.options?.length > 0
    },
    orderedOptions() {
      const opts = this.getOptions()
      const sortByPosition = sortBy(
        opts?.[0]?.name?.match('iPhone') ? 'position' : 'name'
      )

      const sorted = sortByPosition(opts)

      if (this.$isBrStore) {
        return sorted
      }

      return sorted.map((opt) => {
        opt.name = opt.name.replace('650ml', '22oz')
        opt.name = opt.name.replace('950ml', '32oz')
        return opt
      })
    },
    inputName() {
      return this.customInputName || 'device_id'
    },
    inputId() {
      return this.customInputId || 'device_id'
    },
  },
  watch: {
    inputValue(newVal) {
      this.$emit('changedDevice', newVal)
    },
  },
  mounted() {
    if (this.listingSelector?.length)
      this.inputValue = this.orderedOptions?.[0]?.id
  },
  methods: {
    updateDevice(deviceId) {
      this.inputValue = deviceId
    },
    caseTypes(device) {
      // eslint-disable-next-line camelcase
      return (this.product?.case_types ?? []).filter((ct) =>
        ct.devices.map((d) => d.device_id).includes(Number(device.id))
      )
    },
    caseDeviceHasStock(caseDeviceId) {
      return this.productStock.case_devices[caseDeviceId]
    },
    deviceWithoutStock(device) {
      if (!this.product || !this.caseDeviceId) return false
      const caseTypes = this.caseTypes(device)

      const equalSelectedDevice = (d) =>
        // eslint-disable-next-line camelcase
        d.case_device_id === this.caseDeviceId

      const caseType = caseTypes.find((ct) =>
        ct.devices.some(equalSelectedDevice)
      )
      const caseDevice = caseType?.devices?.find(
        // eslint-disable-next-line camelcase
        (dv) => dv.device_id === device.id
        // eslint-disable-next-line camelcase
      )

      return !this.caseDeviceHasStock(caseDevice.case_device_id)
    },
    getOptions() {
      return this.options
    },
  },
}
</script>

<style lang="scss" scoped>
.device-selector__list {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
}

.device-selector__item {
  border: 1px solid #8f8f8f;
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  font-weight: 600;
  color: #8f8f8f;

  &--active {
    border: 1.8px solid $v4_color_primary;
    color: black;
  }

  &--without-stock {
    opacity: 0.7;
  }
}

.device-selector__hint {
  margin-bottom: 10px;

  & > span {
    font-size: 11px;
    text-transform: uppercase;
    color: #929296;
    font-weight: normal;
    margin-right: 5px;
  }
}
</style>
